import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Card, CardHeader, Btn, Input, Table, Badge, PageHeader, POINT_VALUES } from '../../components/ui';
import { Plus } from 'lucide-react';

const INIT = { script_name: '', lot_size: '', point_values: ['0.10', '0.20', '0.50'] };

export default function ScriptMasterPage() {
  const [scripts, setScripts] = useState([]);
  const [form, setForm] = useState(INIT);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => { load(); }, []);
  const load = () => api.get('/masters/scripts').then(r => setScripts(r.data));

  const togglePV = (pv) => {
    setForm(f => ({
      ...f,
      point_values: f.point_values.includes(pv)
        ? f.point_values.filter(v => v !== pv)
        : [...f.point_values, pv]
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.script_name || !form.lot_size) { toast.error('Name and lot size required'); return; }
    if (form.point_values.length === 0) { toast.error('Select at least one point value'); return; }
    setLoading(true);
    try {
      if (editId) { await api.put(`/masters/scripts/${editId}`, { ...form, is_active: true }); toast.success('Updated!'); }
      else { await api.post('/masters/scripts', form); toast.success('Script added!'); }
      setForm(INIT); setShowForm(false); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  const startEdit = (s) => {
    setForm({ script_name: s.script_name, lot_size: s.lot_size, point_values: s.point_values || ['0.10', '0.20', '0.50'] });
    setEditId(s.id); setShowForm(true);
  };

  const del = async (id) => {
    if (!window.confirm('Delete script?')) return;
    await api.delete(`/masters/scripts/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader
        title="Script Master"
        subtitle="Manage tradeable scripts with lot size & point values"
        action={<Btn onClick={() => { setShowForm(v => !v); setEditId(null); setForm(INIT); }}><Plus size={15} /> Add Script</Btn>}
      />

      {showForm && (
        <Card style={{ marginBottom: 22 }}>
          <CardHeader title={editId ? 'Edit Script' : 'Add New Script'} />
          <form onSubmit={submit} style={{ padding: 22 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
              <Input label="Script Name *" value={form.script_name} onChange={e => setForm(f => ({ ...f, script_name: e.target.value }))} placeholder="e.g. NIFTY 50, BANKNIFTY, RELIANCE" />
              <Input label="Lot Size *" type="number" min="1" value={form.lot_size} onChange={e => setForm(f => ({ ...f, lot_size: e.target.value }))} placeholder="e.g. 50, 75, 25" />
            </div>

            {/* Point Value selector */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                Allowed Point Values * <span style={{ color: '#94a3b8', fontWeight: 400 }}>(select all that apply)</span>
              </label>
              <div style={{ display: 'flex', gap: 12 }}>
                {POINT_VALUES.map(pv => {
                  const selected = form.point_values.includes(pv);
                  return (
                    <button key={pv} type="button" onClick={() => togglePV(pv)} style={{
                      padding: '12px 28px', borderRadius: 10, border: '2px solid', cursor: 'pointer', fontWeight: 800, fontSize: 16, transition: 'all 0.15s',
                      borderColor: selected ? '#2563eb' : '#e2e8f0',
                      background: selected ? 'linear-gradient(135deg,#2563eb,#1d4ed8)' : '#f8fafc',
                      color: selected ? '#fff' : '#94a3b8',
                      boxShadow: selected ? '0 4px 12px rgba(37,99,235,0.3)' : 'none',
                      transform: selected ? 'translateY(-1px)' : 'none'
                    }}>
                      {pv}
                    </button>
                  );
                })}
              </div>
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                These point values will be shown as options when entering buy/sell orders for this script.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <Btn type="submit" disabled={loading}>{loading ? 'Saving…' : editId ? 'Update' : 'Add Script'}</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader title="All Scripts" subtitle={`${scripts.length} scripts`} />
        <Table
          headers={['Script Name', 'Lot Size', 'Point Values', 'Actions']}
          rows={scripts.map(s => [
            <strong style={{ fontSize: 14 }}>{s.script_name}</strong>,
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{s.lot_size}</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>units/lot</span>
            </div>,
            <div style={{ display: 'flex', gap: 6 }}>
              {(s.point_values || []).map(pv => <Badge key={pv} color="purple">{pv}</Badge>)}
            </div>,
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn size="sm" variant="ghost" onClick={() => startEdit(s)}>Edit</Btn>
              <Btn size="sm" variant="danger" onClick={() => del(s.id)}>Delete</Btn>
            </div>
          ])}
          empty="No scripts found. Add your first script."
        />
      </Card>
    </div>
  );
}
